<template>
  <div
    v-show="dragging"
    ref="root"
    class="grid-view__row-dragging-container"
    :style="{ left: offset + 'px' }"
  >
    <div
      class="grid-view__row-dragging"
      :style="{ width: width + 'px', top: draggingTop + 'px' }"
    ></div>
    <div
      class="grid-view__row-target"
      :style="{ width: width + 'px', top: targetTop + 'px' }"
    ></div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { notifyIf } from '@baserow/modules/core/utils/error'
import { startPointerDrag } from '@baserow/modules/core/utils/dragAndDrop'
import gridViewHelpers from '@baserow/modules/database/mixins/gridViewHelpers'

export default {
  name: 'GridViewRowDragging',
  mixins: [gridViewHelpers],
  props: {
    table: {
      type: Object,
      required: true,
    },
    view: {
      type: Object,
      required: true,
    },
    allVisibleFields: {
      type: Array,
      required: true,
    },
    allFieldsInTable: {
      type: Array,
      required: true,
    },
    getScrollElement: {
      type: Function,
      required: true,
    },
    offset: {
      type: Number,
      required: false,
      default: () => 0,
    },
    /**
     * Origem Flow: campo do agrupamento único no modo banda. Quando
     * presente, o alvo do arrasto é calculado pelo DOM (as bandas de grupo
     * quebram a conta geométrica do upstream) e soltar em outro grupo
     * também troca o valor do campo agrupado (ex.: Status), como no
     * ClickUp.
     */
    bandField: {
      type: Object,
      required: false,
      default: null,
    },
  },
  emits: ['scroll', 'refresh'],
  data() {
    return {
      // Indicates if the user is dragging a row to another position.
      dragging: false,
      // The row object that is being dragged.
      row: null,
      // The top position of the row.
      rowTop: 0,
      // The row where the dragged row must be placed before.
      targetRow: null,
      // The horizontal starting position of the mouse.
      mouseStart: 0,
      // The position of the dragging animation.
      draggingTop: 0,
      // The position of the target indicator where the field is going to be moved to.
      targetTop: 0,
      // The mouse move event.
      lastMoveEvent: null,
      // Origem Flow (modo banda): valor do campo agrupado no ponto onde a
      // linha vai ser solta (opção do single select ou null).
      bandTargetGroupValue: null,
      bandHasTarget: false,
      // Indicates if the user is auto scrolling at the moment.
      autoScrolling: false,
      // Event handler references for cleanup
      moveEvent: null,
      upEvent: null,
      keydownEvent: null,
      scrollTimeout: null,
    }
  },
  computed: {
    width() {
      return (
        this.allVisibleFields.reduce(
          (value, field) => this.getFieldWidth(field) + value,
          0
        ) + this.gridViewRowDetailsWidth
      )
    },
    rowHeight() {
      return this.$store.getters[this.storePrefix + 'view/grid/getRowHeight']
    },
    bufferStartIndex() {
      return this.$store.getters[
        this.storePrefix + 'view/grid/getBufferStartIndex'
      ]
    },
    rowsCount() {
      return this.$store.getters[this.storePrefix + 'view/grid/getCount']
    },
    allRows() {
      return this.$store.getters[this.storePrefix + 'view/grid/getAllRows']
    },
  },
  beforeUnmount() {
    this.cancel()
  },
  methods: {
    getRowTop(rowId) {
      const index = this.allRows.findIndex((row) => row.id === rowId)
      if (index < 0) {
        return 0
      }

      return this.bufferStartIndex * this.rowHeight + index * this.rowHeight
    },
    /**
     * Called when the row dragging must start. It will register the global mouse
     * move, mouse up events and keyup events so that the user can drag the field to
     * the correct position.
     */
    start(row, event) {
      const element = this.getScrollElement()

      this.row = row
      this.startRowTop = this.getRowTop(row.id) - element.scrollTop
      if (this.bandField) {
        // No modo banda a conta geométrica não vale (bandas e cabeçalhos
        // de grupo entre as linhas); usa a posição real no DOM.
        const rowElement = this.getBandRowElement(row.id)
        if (rowElement) {
          this.startRowTop =
            rowElement.getBoundingClientRect().top -
            element.getBoundingClientRect().top
        }
        this.bandTargetGroupValue = null
        this.bandHasTarget = false
      }
      this.targetRowId = row.id
      this.dragging = true
      this.mouseStart = event.clientY
      this.draggingTop = 0
      this.targetTop = 0

      // Origem Flow: o núcleo de arrasto cuida de mouse/caneta/toque
      // (long-press), Escape e cursor global.
      this.dragController = startPointerDrag(event, {
        onStart: (startEvent) => this.move(startEvent, false),
        onMove: (moveEvent) => this.move(moveEvent),
        onUp: (upEvent, engaged) => {
          if (engaged) {
            this.up(upEvent)
          } else {
            this.cancel()
          }
        },
        onCancel: () => this.cancel(),
      })
      this.move(event, false)
    },
    /**
     * The move method is called when every time the user moves the mouse while
     * dragging a row. It can also be called while auto scrolling.
     */
    move(event = null, startAutoScroll = true) {
      if (event !== null) {
        event.preventDefault()
        this.lastMoveEvent = event
      } else {
        event = this.lastMoveEvent
      }

      // This is the vertically scrollable element.
      const element = this.getScrollElement()
      const elementRect = element.getBoundingClientRect()
      const elementHeight = elementRect.bottom - elementRect.top

      // Calculate the top position of the dragging effect. Note that this effect lays
      // over the vertically scrollable rows.
      this.draggingTop = Math.max(
        0,
        Math.min(
          this.startRowTop + event.clientY - this.mouseStart,
          elementHeight - this.rowHeight
        )
      )

      // Calculate before which row we want to place the row that is currently being
      // dragged. We also calculate target top position which indicates at which
      // position the row is going to be placed. Note that the target effect lays over
      // the vertically scrollable rows.
      if (this.bandField) {
        this.moveBand(event, elementRect)
      } else {
        const mouseTop = event.clientY - elementRect.top + element.scrollTop
        const rowIndex = Math.max(
          0,
          Math.min(Math.round(mouseTop / this.rowHeight), this.rowsCount)
        )
        this.targetTop = rowIndex * this.rowHeight - element.scrollTop
        const beforeRow = this.allRows[rowIndex - this.bufferStartIndex]
        this.targetRow = beforeRow === undefined ? null : beforeRow
      }

      // If the user is not already auto scrolling, which happens while dragging and
      // moving the element close to the end of the view port at the top or bottom
      // side, we might need to initiate that process.
      if (!this.autoScrolling || !startAutoScroll) {
        const side = Math.ceil((elementHeight / 100) * 10)
        const autoScrollMouseTop = event.clientY - elementRect.top
        const autoScrollMouseBottom = elementHeight - autoScrollMouseTop
        let speed = 0

        if (autoScrollMouseTop < side) {
          speed = -(6 - Math.ceil((Math.max(0, autoScrollMouseTop) / side) * 6))
        } else if (autoScrollMouseBottom < side) {
          speed = 6 - Math.ceil((Math.max(0, autoScrollMouseBottom) / side) * 6)
        }

        // If the speed is either a position or negative, so not 0, we know that we
        // need to start auto scrolling.
        if (speed !== 0) {
          this.autoScrolling = true
          this.$emit('scroll', { pixelY: speed, pixelX: 0 })
          this.scrollTimeout = setTimeout(() => {
            this.move(null, false)
          }, 1)
        } else {
          this.autoScrolling = false
        }
      }
    },
    /**
     * Origem Flow (modo banda): elemento DOM da linha; só existe uma cópia
     * porque no modo banda todas as colunas vivem na seção direita.
     */
    getBandRowElement(rowId) {
      const gridRoot = this.$el.closest('.grid-view')
      if (!gridRoot) {
        return null
      }
      return gridRoot.querySelector(`.grid-view__row[data-row-id="${rowId}"]`)
    },
    /**
     * Origem Flow (modo banda): calcula o alvo do arrasto pelas posições
     * reais das linhas no DOM. Na fronteira entre dois grupos, o lado mais
     * próximo do cursor decide se a linha vai para o fim do grupo de cima
     * ou para o topo do grupo de baixo — nos dois casos a linha é inserida
     * antes da mesma linha; muda apenas o valor do campo agrupado.
     */
    moveBand(event, elementRect) {
      const gridRoot = this.$el.closest('.grid-view')
      const rowElements = gridRoot
        ? [...gridRoot.querySelectorAll('.grid-view__row[data-row-id]')]
        : []
      const rowsById = {}
      this.allRows.forEach((row) => {
        rowsById[row.id] = row
      })
      const items = rowElements
        .map((el) => ({
          rect: el.getBoundingClientRect(),
          row: rowsById[parseInt(el.dataset.rowId, 10)],
        }))
        .filter((item) => item.row !== undefined)
      // Origem Flow: grupos VAZIOS não têm linhas para servir de alvo — a
      // banda (cabeçalho do grupo) vira o alvo: soltar dentro da região de um
      // grupo sem tarefas troca o status para ele (antes era zona morta e o
      // drop resolvia para o grupo vizinho).
      const bandElements = gridRoot
        ? [...gridRoot.querySelectorAll('[data-band-group]')]
        : []
      let bandAbove = null
      for (const el of bandElements) {
        const rect = el.getBoundingClientRect()
        if (rect.top <= event.clientY) {
          bandAbove = { rect, groupId: el.dataset.bandGroup }
        }
      }
      if (bandAbove !== null) {
        const groupId = bandAbove.groupId === '' ? null : parseInt(bandAbove.groupId, 10)
        const groupIsEmpty = !this.allRows.some((row) => {
          const value = row[`field_${this.bandField.id}`] || null
          return (value === null ? null : value.id) === groupId
        })
        if (groupIsEmpty) {
          const option =
            groupId === null
              ? null
              : (this.bandField.select_options ?? []).find(
                  (o) => o.id === groupId
                ) ?? null
          if (groupId === null || option !== null) {
            // Antes de qual linha inserir: a primeira linha ABAIXO do cursor
            // (fim da região do grupo vazio); nenhuma = fim da tabela.
            const belowItem = items.find(
              (item) => event.clientY < item.rect.top + item.rect.height / 2
            )
            this.targetRow = belowItem ? belowItem.row : null
            this.bandTargetGroupValue = option
              ? { id: option.id, value: option.value, color: option.color }
              : null
            this.bandHasTarget = true
            this.targetTop = bandAbove.rect.bottom - elementRect.top
            return
          }
        }
      }

      if (items.length === 0) {
        this.targetRow = null
        this.bandHasTarget = false
        return
      }
      let above = null
      let below = null
      for (const item of items) {
        if (event.clientY < item.rect.top + item.rect.height / 2) {
          below = item
          break
        }
        above = item
      }
      const groupValueOf = (row) => row[`field_${this.bandField.id}`] || null
      const groupIdOf = (row) => {
        const value = groupValueOf(row)
        return value === null ? null : value.id
      }
      let targetTopClient
      if (below === null) {
        this.targetRow = null
        this.bandTargetGroupValue = groupValueOf(above.row)
        targetTopClient = above.rect.bottom
      } else if (above === null || groupIdOf(above.row) === groupIdOf(below.row)) {
        this.targetRow = below.row
        this.bandTargetGroupValue = groupValueOf(below.row)
        targetTopClient = below.rect.top
      } else {
        this.targetRow = below.row
        const distAbove = event.clientY - above.rect.bottom
        const distBelow = below.rect.top - event.clientY
        if (distAbove <= distBelow) {
          this.bandTargetGroupValue = groupValueOf(above.row)
          targetTopClient = above.rect.bottom
        } else {
          this.bandTargetGroupValue = groupValueOf(below.row)
          targetTopClient = below.rect.top
        }
      }
      this.bandHasTarget = true
      this.targetTop = targetTopClient - elementRect.top
    },
    /**
     * Origem Flow (modo banda): solta a linha — remove as ordenações da
     * view (a ordem manual passa a valer, como no ClickUp), troca o valor
     * do campo agrupado se o destino é outro grupo e move a linha.
     */
    async upBand() {
      if (!this.bandHasTarget) {
        return
      }
      const row = this.row
      const before = this.targetRow
      const field = this.bandField
      const newValue = this.bandTargetGroupValue
      const oldValue = row[`field_${field.id}`] || null
      const groupChanged =
        (oldValue === null ? null : oldValue.id) !==
        (newValue === null ? null : newValue.id)

      // Sem mudança de grupo e soltando no mesmo lugar não há o que fazer.
      if (!groupChanged) {
        const allRows =
          this.$store.getters[this.storePrefix + 'view/grid/getAllRows']
        if (before !== null) {
          if (before.id === row.id) {
            return
          }
          const index = allRows.findIndex((r) => r.id === before.id)
          const previous = allRows[index - 1]
          if (previous && previous.id === row.id) {
            return
          }
        } else if (
          allRows.length > 0 &&
          allRows[allRows.length - 1].id === row.id
        ) {
          return
        }
      }

      const element = this.getScrollElement()
      const getScrollTop = () => element.scrollTop
      const hadSortings = this.view.sortings.length > 0
      try {
        for (const sort of [...this.view.sortings]) {
          await this.$store.dispatch('view/deleteSort', {
            view: this.view,
            sort,
          })
        }
        if (groupChanged) {
          await this.$store.dispatch(
            this.storePrefix + 'view/grid/updateRowValue',
            {
              table: this.table,
              view: this.view,
              fields: this.allFieldsInTable,
              row,
              field,
              value: newValue,
              oldValue,
            }
          )
        }
        await this.$store.dispatch(this.storePrefix + 'view/grid/moveRow', {
          table: this.table,
          grid: this.view,
          fields: this.allFieldsInTable,
          getScrollTop,
          row,
          before,
        })
        if (hadSortings) {
          // Sem as ordenações a ordem manual manda; recarrega para o
          // servidor devolver as linhas já na nova ordem.
          this.$emit('refresh')
        }
      } catch (error) {
        notifyIf(error, 'row')
      }
    },
    /**
     * Can be called when the current dragging state needs to be stopped. It will
     * remove all the created event listeners and timeouts.
     */
    cancel() {
      this.dragging = false
      this.dragController?.dispose()
      clearTimeout(this.scrollTimeout)
    },
    /**
     * Called when the user releases the mouse on a the desired position. It will
     * calculate the new position of the row in the list and if it has changed
     * position, then the order in the row options is updated accordingly.
     */
    async up(event) {
      this.cancel()

      if (this.bandField) {
        return this.upBand()
      }

      // We don't need to do anything if the row must be placed before or after itself
      // because that wouldn't change the position.
      if (this.targetRow !== null) {
        // If the row must be placed before itself.
        if (this.row.id === this.targetRow.id) {
          return
        }

        // If the row needs to be placed after itself.
        const allRows =
          this.$store.getters[this.storePrefix + 'view/grid/getAllRows']
        const index = allRows.findIndex((r) => r.id === this.targetRow.id)
        const after = allRows[index - 1]
        if (after && this.row.id === after.id) {
          return
        }
      }

      const element = this.getScrollElement()
      const getScrollTop = () => element.scrollTop

      try {
        await this.$store.dispatch(this.storePrefix + 'view/grid/moveRow', {
          table: this.table,
          grid: this.view,
          fields: this.allFieldsInTable,
          getScrollTop,
          row: this.row,
          before: this.targetRow,
        })
      } catch (error) {
        notifyIf(error, 'row')
      }
    },
  },
}
</script>
